import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPlatformLocationFeatureV1Service, serviceName } from '../service';
import { CheckFeatureNotificationStatusIO, ServiceQueries } from '../types';
import { usePlatformLocationFeatureV1Queries } from './use-platform-location-feature-v1-queries';
import {
  UsePlatformLocationFeatureV1QueryEndpointArgs,
  usePlatformLocationFeatureV1Query,
} from './use-platform-location-feature-v1-query';

/**
 * A hook that returns a query for the `CheckFeatureNotificationStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckFeatureNotificationStatusQuery = <E = unknown, D = CheckFeatureNotificationStatusIO['output']>(
  args: UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckFeatureNotificationStatus', E, D>
) =>
  usePlatformLocationFeatureV1Query({
    endpointName: 'CheckFeatureNotificationStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `CheckFeatureNotificationStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useCheckFeatureNotificationStatusQueries = <E = unknown, D = CheckFeatureNotificationStatusIO['output']>(
  argsArr: UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckFeatureNotificationStatus', E, D>[]
) =>
  usePlatformLocationFeatureV1Queries(
    argsArr.map((args) => ({
      endpointName: 'CheckFeatureNotificationStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `CheckFeatureNotificationStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchCheckFeatureNotificationStatus = () => {
  const queryClient = useQueryClient();

  const fetchCheckFeatureNotificationStatus = async <E = unknown, D = CheckFeatureNotificationStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UsePlatformLocationFeatureV1QueryEndpointArgs<'CheckFeatureNotificationStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      CheckFeatureNotificationStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckFeatureNotificationStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        CheckFeatureNotificationStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'CheckFeatureNotificationStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'CheckFeatureNotificationStatus'>({
          serviceName,
          endpointName: 'CheckFeatureNotificationStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaPlatformLocationFeatureV1Service.CheckFeatureNotificationStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchCheckFeatureNotificationStatus;
};
